/**
 * React Query hooks for Meta ad browsing
 */

import { useQuery } from '@tanstack/react-query';

import { getCampaigns, getAdSets, getAds } from '@/services/meta';
import type { MetaDateRange } from '@/types/meta';

// ============================================================================
// QUERY KEY FACTORIES
// ============================================================================

export const metaBrowseKeys = {
  all: ['meta-browse'] as const,
  campaigns: (teamId: string, dateRange?: MetaDateRange) =>
    [...metaBrowseKeys.all, 'campaigns', teamId, dateRange] as const,
  adSets: (teamId: string, campaignId: string, dateRange?: MetaDateRange) =>
    [...metaBrowseKeys.all, 'adsets', teamId, campaignId, dateRange] as const,
  ads: (teamId: string, adSetId: string) =>
    [...metaBrowseKeys.all, 'ads', teamId, adSetId] as const,
};

// ============================================================================
// CAMPAIGNS HOOK
// ============================================================================

interface UseCampaignsOptions {
  teamId: string;
  dateRange?: MetaDateRange;
  enabled?: boolean;
}

/**
 * Hook to fetch campaigns for the team's Meta ad account
 */
export function useCampaigns({ teamId, dateRange, enabled = true }: UseCampaignsOptions) {
  return useQuery({
    queryKey: metaBrowseKeys.campaigns(teamId, dateRange),
    queryFn: async () => {
      const response = await getCampaigns(teamId, dateRange);
      return response.campaigns;
    },
    enabled: !!teamId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - cache retention
  });
}

// ============================================================================
// AD SETS HOOK
// ============================================================================

interface UseAdSetsOptions {
  teamId: string;
  campaignId: string | null;
  dateRange?: MetaDateRange;
  enabled?: boolean;
}

/**
 * Hook to fetch ad sets for a specific campaign
 */
export function useAdSets({
  teamId,
  campaignId,
  dateRange,
  enabled = true,
}: UseAdSetsOptions) {
  return useQuery({
    queryKey: metaBrowseKeys.adSets(teamId, campaignId ?? '', dateRange),
    queryFn: async () => {
      if (!campaignId) {
        return [];
      }
      const response = await getAdSets(teamId, campaignId, dateRange);
      return response.adSets;
    },
    enabled: !!teamId && !!campaignId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - cache retention
  });
}

// ============================================================================
// ADS HOOK
// ============================================================================

interface UseAdsOptions {
  teamId: string;
  adSetId: string | null;
  enabled?: boolean;
}

/**
 * Hook to fetch ads for a specific ad set
 */
export function useAds({ teamId, adSetId, enabled = true }: UseAdsOptions) {
  return useQuery({
    queryKey: metaBrowseKeys.ads(teamId, adSetId ?? ''),
    queryFn: async () => {
      if (!adSetId) {
        return [];
      }
      const response = await getAds(teamId, adSetId);
      return response.ads;
    },
    enabled: !!teamId && !!adSetId && enabled,
    staleTime: 5 * 60 * 1000, // 5 minutes - data considered fresh
    gcTime: 10 * 60 * 1000, // 10 minutes - cache retention
  });
}
